import React, { useState, useEffect, useCallback } from 'react';
import { PenaltyRecord, MemberPenalty, calculatePenaltyLevel, PENALTY_LEVELS } from '@/types/penalty';
import { PenaltyContext } from './PenaltyContext';
import { usePenalty } from '@/hooks/use-penalty-hook';
import { supabase, hasSupabaseAuth } from '@/integrations/supabase/client';

const PENALTY_TABLE = 'penalty_state';
const PENALTY_DATA_VERSION = 1;

export function PenaltyProvider({ children }: { children: React.ReactNode }) {
  const [penalties, setPenalties] = useState<MemberPenalty[]>([]);

  // Load penalties from Supabase on mount (skip if not authenticated with Supabase)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!hasSupabaseAuth()) return;
      try {
        // TODO: Implement penalty state when table is available
        const data = null;
        const stored = null;
        if (
          stored &&
          stored.version === PENALTY_DATA_VERSION &&
          stored.penalties &&
          !cancelled
        ) {
          setPenalties(stored.penalties as MemberPenalty[]);
        }
      } catch (error) {
        console.error('Error loading penalties from Supabase:', error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Save penalties to Supabase whenever they change (only if authenticated)
  useEffect(() => {
    const save = async () => {
      if (!hasSupabaseAuth()) return;
      try {
        const dataToStore = {
          version: PENALTY_DATA_VERSION,
          penalties,
          lastUpdated: new Date().toISOString(),
        };
        // TODO: Save penalty state when table is available
      } catch (error) {
        console.error('Error saving penalties to Supabase:', error);
      }
    };
    void save();
  }, [penalties]);

  const addPenalty = (penaltyData: Omit<PenaltyRecord, 'id' | 'timestamp'>) => {
    const newRecord: PenaltyRecord = {
      ...penaltyData,
      id: `penalty_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };

    setPenalties(prev => {
      const existingPenalty = prev.find(p => p.memberId === penaltyData.memberId);
      
      if (existingPenalty) {
        // Update existing penalty
        const updatedRecords = [...existingPenalty.records, newRecord];
        const newTotalDots = updatedRecords.length;
        const newLevel = calculatePenaltyLevel(newTotalDots);
        
        const updatedPenalty: MemberPenalty = {
          ...existingPenalty,
          totalDots: newTotalDots,
          activePenaltyLevel: newLevel.level as 0 | 1 | 2 | 3,
          records: updatedRecords
        };

        // Set penalty dates if entering a penalty level
        if (newLevel.level > 0) {
          const startDate = new Date();
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (newLevel.durationWeeks * 7));
          
          updatedPenalty.penaltyStartDate = startDate.toISOString();
          updatedPenalty.penaltyEndDate = endDate.toISOString();
        }

        return prev.map(p => p.memberId === penaltyData.memberId ? updatedPenalty : p);
      } else {
        // Create new penalty
        const newPenalty: MemberPenalty = {
          memberId: penaltyData.memberId,
          totalDots: 1,
          activePenaltyLevel: 0,
          records: [newRecord]
        };

        return [...prev, newPenalty];
      }
    });
  };

  const getMemberPenalty = (memberId: string): MemberPenalty | null => {
    return penalties.find(p => p.memberId === memberId) || null;
  };

  const clearExpiredPenalties = () => {
    const now = new Date();
    setPenalties(prev => prev.map(penalty => {
      if (penalty.penaltyEndDate && new Date(penalty.penaltyEndDate) <= now) {
        return {
          ...penalty,
          activePenaltyLevel: 0 as const,
          penaltyStartDate: undefined,
          penaltyEndDate: undefined
        };
      }
      return penalty;
    }));
  };

  const removePenalty = (memberId: string, recordId: string) => {
    setPenalties(prev => prev.map(penalty => {
      if (penalty.memberId === memberId) {
        const updatedRecords = penalty.records.filter(r => r.id !== recordId);
        const newTotalDots = updatedRecords.length;
        const newLevel = calculatePenaltyLevel(newTotalDots);
        
        const updatedPenalty: MemberPenalty = {
          ...penalty,
          totalDots: newTotalDots,
          activePenaltyLevel: newLevel.level as 0 | 1 | 2 | 3,
          records: updatedRecords
        };

        // Update penalty dates
        if (newLevel.level > 0) {
          const startDate = new Date();
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (newLevel.durationWeeks * 7));
          
          updatedPenalty.penaltyStartDate = startDate.toISOString();
          updatedPenalty.penaltyEndDate = endDate.toISOString();
        } else {
          updatedPenalty.penaltyStartDate = undefined;
          updatedPenalty.penaltyEndDate = undefined;
        }

        return updatedPenalty;
      }
      return penalty;
    }));
  };

  const getPenaltyStats = useCallback(() => {
    const stats = {
      totalPenalties: penalties.length,
      activePenalties: penalties.filter(p => p.activePenaltyLevel > 0).length,
      totalDots: penalties.reduce((sum, p) => sum + p.totalDots, 0),
      penaltiesByType: {
        late_cancel: 0,
        no_show: 0
      }
    };

    penalties.forEach(penalty => {
      penalty.records.forEach(record => {
        if (record.type === 'late_cancel') {
          stats.penaltiesByType.late_cancel++;
        } else if (record.type === 'no_show') {
          stats.penaltiesByType.no_show++;
        }
      });
    });

    return stats;
  }, [penalties]);

  // Clear expired penalties on mount and periodically
  useEffect(() => {
    clearExpiredPenalties();
    const interval = setInterval(clearExpiredPenalties, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <PenaltyContext.Provider value={{
      penalties,
      addPenalty,
      getMemberPenalty,
      clearExpiredPenalties,
      removePenalty,
      getPenaltyStats
    }}>
      {children}
    </PenaltyContext.Provider>
  );
}
